/** Service Requests - central estate view (Demo Mode only). Where the central Estate
 * Management team triages, manages and resolves issues raised by sites. Distinct from the
 * site-facing Service Requests screen under Site Operations, which is scoped to a single
 * site's own requests. No backend service-request system exists yet - all data is static mock
 * data, matching the same "seeded illustrative numbers" approach used by
 * src/pages/EstateManagement. */

import { useState } from 'react';
import { Box, Chip, Grid, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { Assignment, ErrorOutline, PriorityHigh, ListAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';

const MOCK_REQUESTS = [
  { id: 'SR-1042', site: 'Riverside', zone: 'Chilled Store', asset: 'TF-RVSD-STR-0003', type: 'Fault', priority: 'High', status: 'In Progress', raisedBy: 'J. Adeyemi', assignedTo: 'CaterCare Services Ltd', created: '2026-06-01', target: '2026-06-05', sla: 'On Track' },
  { id: 'SR-1041', site: 'Uptown', zone: 'Coffee Bar', asset: 'TF-UPTN-BAR-0022', type: 'Breakdown', priority: 'Critical', status: 'Awaiting Supplier', raisedBy: 'M. Doyle', assignedTo: 'La Marzocco UK', created: '2026-05-29', target: '2026-05-31', sla: 'Breached' },
  { id: 'SR-1039', site: 'St Mary\'s School', zone: 'Main Kitchen', asset: 'TF-STMARY-KIT-0042', type: 'Maintenance Required', priority: 'Medium', status: 'Resolved', raisedBy: 'Site Team', assignedTo: 'CaterCare Services Ltd', created: '2026-05-20', target: '2026-05-27', sla: 'Met' },
  { id: 'SR-1035', site: 'Southgate', zone: 'Main Kitchen', asset: '-', type: 'Missing Equipment', priority: 'Low', status: 'New', raisedBy: 'Site Team', assignedTo: '-', created: '2026-05-15', target: '2026-06-15', sla: 'On Track' },
  { id: 'SR-1030', site: 'Downtown', zone: 'Main Kitchen', asset: 'TF-DWTN-KIT-0014', type: 'Inspection Required', priority: 'Medium', status: 'Triaged', raisedBy: 'Estate Team', assignedTo: 'Unassigned', created: '2026-05-10', target: '2026-06-01', sla: 'On Track' },
];

const priorityColour = (priority: string) => {
  if (priority === 'Critical') return 'error';
  if (priority === 'High') return 'warning';
  if (priority === 'Medium') return 'info';
  return 'default';
};

const slaColour = (sla: string) => {
  if (sla === 'Breached') return 'error';
  if (sla === 'On Track') return 'success';
  return 'default';
};

const ServiceRequests = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [requestsPage, setRequestsPage] = useState(0);

  const open = MOCK_REQUESTS.filter((r) => !['Resolved', 'Closed', 'Cancelled'].includes(r.status)).length;
  const breached = MOCK_REQUESTS.filter((r) => r.sla === 'Breached').length;
  const critical = MOCK_REQUESTS.filter((r) => r.priority === 'Critical').length;

  const TILES = [
    { label: t('serviceRequestsOpen', 'Open requests'), value: `${open}`, subtitle: t('serviceRequestsOpenSub', 'Currently unresolved'), icon: <Assignment />, color: '#1976d2' },
    { label: t('serviceRequestsBreached', 'SLA breached'), value: `${breached}`, subtitle: t('serviceRequestsBreachedSub', 'Past target date'), icon: <ErrorOutline />, color: '#d32f2f' },
    { label: t('serviceRequestsCritical', 'Critical priority'), value: `${critical}`, subtitle: t('serviceRequestsCriticalSub', 'Highest urgency'), icon: <PriorityHigh />, color: '#ed6c02' },
    { label: t('serviceRequestsTotal', 'Total requests (all time)'), value: `${MOCK_REQUESTS.length}`, subtitle: t('serviceRequestsTotalSub', 'Across the estate'), icon: <ListAlt />, color: '#9c27b0' },
  ];

  return (
    <PageArea>
      <PageTitle>
        {t('serviceRequestsTitle', 'Service Requests')}
      </PageTitle>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        {TILES.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={3}
            key={tile.label}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Section>
          <TableHeader>
            {t('serviceRequestsListTitle', 'All estate requests')}
            <Pill text={`${MOCK_REQUESTS.length} ${t('serviceRequestsLabel', 'requests')}`} />
          </TableHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('serviceRequestsId', 'Request ID')}</TableCell>
                  <TableCell>{t('estateManagementStore', 'Location')}</TableCell>
                  <TableCell>{t('storeZonesName', 'Zone')}</TableCell>
                  <TableCell>{t('siteEquipmentAssetNumber', 'Equipment Asset')}</TableCell>
                  <TableCell>{t('serviceRequestsType', 'Issue Type')}</TableCell>
                  <TableCell>{t('serviceRequestsPriority', 'Priority')}</TableCell>
                  <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
                  <TableCell>{t('serviceRequestsRaisedBy', 'Raised By')}</TableCell>
                  <TableCell>{t('serviceRequestsAssignedTo', 'Assigned To')}</TableCell>
                  <TableCell>{t('serviceRequestsCreated', 'Created')}</TableCell>
                  <TableCell>{t('serviceRequestsTarget', 'Target Date')}</TableCell>
                  <TableCell>{t('serviceRequestsSla', 'SLA Status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOCK_REQUESTS.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.site}</TableCell>
                    <TableCell>{row.zone}</TableCell>
                    <TableCell>{row.asset}</TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>
                      <Chip label={row.priority}
                        size="small"
                        color={priorityColour(row.priority)}
                        variant="outlined" />
                    </TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>{row.raisedBy}</TableCell>
                    <TableCell>{row.assignedTo}</TableCell>
                    <TableCell>{row.created}</TableCell>
                    <TableCell>{row.target}</TableCell>
                    <TableCell>
                      <Chip label={row.sla}
                        size="small"
                        color={slaColour(row.sla)}
                        variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination page={requestsPage}
            setPage={setRequestsPage}
            maxPages={1}
            useNoDataMessage={false}
            breakpointForChangeDisplay={120000} />
        </Section>
      </Box>
    </PageArea>
  );
};

export default ServiceRequests;
